/**
 * Full-screen orientation tour shown exactly once when a learner enters their
 * first unguided lesson. Reuses the same `useTargetRect` + four-panel scrim
 * pattern as `TourOverlay`, but the two interactive steps (collapse / expand)
 * pass pointer events through to the real button so the learner physically
 * performs the action instead of reading about it.
 */
import type { CSSProperties } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DESC_SIZES,
  META_SIZES,
  TITLE_SIZES,
  useAccessibility,
} from '@/accessibility/use-accessibility'
import { cn } from '@/lib/utils'
import { type TargetRect, useTargetRect } from '@/player/use-target-rect'

// ── Constants ─────────────────────────────────────────────────────────────────

const SCRIM = 'bg-[oklch(0_0_0/0.6)]'
const SPOTLIGHT_PAD = 8
const SPOTLIGHT_GLOW = 'tour-glow 1.4s ease-in-out infinite'
// Wait for screen transitions before fading the overlay back in.
const SETTLE_MS = 380

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * - `narrative`  — full-screen dark scrim, tap anywhere or press Next
 * - `spotlight`  — four-panel scrim + glowing halo on the target, Next button
 * - `collapse`   — spotlight the ↓ button; scrim passes through clicks;
 *                   auto-advances when `expanded` becomes false
 * - `expand`     — spotlight the blue pill; scrim passes through clicks;
 *                   auto-advances when `expanded` becomes true
 */
export type UnguidedStepKind = 'narrative' | 'spotlight' | 'collapse' | 'expand'

export interface UnguidedTourStep {
  kind: UnguidedStepKind
  /** `data-tour` value of the element to spotlight. Omit for narrative steps. */
  target?: string
  heading: string
  body: string
}

interface UnguidedTourProps {
  readonly steps: UnguidedTourStep[]
  readonly stepIndex: number
  /** Current expanded/collapsed state of the task panel. */
  readonly expanded: boolean
  readonly onAdvance: () => void
  readonly onDismiss: () => void
}

// ── Spotlight ─────────────────────────────────────────────────────────────────

/**
 * Four dark panels boxing the target, plus the animated glow halo.
 *
 * `passThrough` makes every panel `pointer-events-none` so taps land on the
 * real element underneath — used for the collapse and expand interactive steps.
 * When false the panels block interaction; the learner advances via the card's
 * Next button (no onClick on the panels themselves — avoids a11y div-as-button).
 */
function Spotlight({
  rect,
  passThrough,
}: Readonly<{
  rect: TargetRect
  passThrough: boolean
}>) {
  const scrim = cn(SCRIM, 'absolute', passThrough ? 'pointer-events-none' : 'pointer-events-auto')
  const halo: CSSProperties = {
    top: rect.top - SPOTLIGHT_PAD,
    left: rect.left - SPOTLIGHT_PAD,
    width: rect.width + SPOTLIGHT_PAD * 2,
    height: rect.height + SPOTLIGHT_PAD * 2,
    borderRadius: 12,
    animation: SPOTLIGHT_GLOW,
  }

  return (
    <>
      <div
        aria-hidden="true"
        className={scrim}
        style={{ top: 0, left: 0, right: 0, height: Math.max(0, rect.top - SPOTLIGHT_PAD) }}
      />
      <div
        aria-hidden="true"
        className={scrim}
        style={{
          top: rect.top - SPOTLIGHT_PAD,
          left: 0,
          width: Math.max(0, rect.left - SPOTLIGHT_PAD),
          height: rect.height + SPOTLIGHT_PAD * 2,
        }}
      />
      <div
        aria-hidden="true"
        className={scrim}
        style={{
          top: rect.top - SPOTLIGHT_PAD,
          left: rect.left + rect.width + SPOTLIGHT_PAD,
          right: 0,
          height: rect.height + SPOTLIGHT_PAD * 2,
        }}
      />
      <div
        aria-hidden="true"
        className={scrim}
        style={{
          top: rect.top + rect.height + SPOTLIGHT_PAD,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      />
      <div aria-hidden="true" className="absolute pointer-events-none" style={halo} />
    </>
  )
}

// ── Card position ─────────────────────────────────────────────────────────────

const CARD_GAP = 14

function cardStyle(rect: TargetRect | null, vpHeight: number): CSSProperties {
  if (!rect) {
    // Narrative: anchor at the bottom so it never obscures the task panel content
    return { left: 0, right: 0, bottom: 16 }
  }
  const targetMidY = rect.top + rect.height / 2
  if (targetMidY < vpHeight * 0.5) {
    // Target in upper half → card below
    return { left: 0, right: 0, top: rect.top + rect.height + SPOTLIGHT_PAD + CARD_GAP }
  }
  // Target in lower half → card above
  return { left: 0, right: 0, bottom: vpHeight - rect.top + SPOTLIGHT_PAD + CARD_GAP }
}

// ── Component ─────────────────────────────────────────────────────────────────

export function UnguidedTour({
  steps,
  stepIndex,
  expanded,
  onAdvance,
  onDismiss,
}: UnguidedTourProps) {
  const { t } = useTranslation('player')
  const { fontScale } = useAccessibility()
  const step = steps[stepIndex]
  const [settling, setSettling] = useState(true)
  const rect = useTargetRect(step?.target)
  const vpHeight = window.visualViewport?.height ?? window.innerHeight

  // Briefly hide the overlay between steps so the screen has time to settle.
  // Reading step.kind in the body satisfies biome's exhaustive-deps rule while
  // also giving interactive steps a slightly longer window for panel animation.
  useEffect(() => {
    const extra = step?.kind === 'collapse' || step?.kind === 'expand' ? 80 : 0
    setSettling(true)
    const id = setTimeout(() => setSettling(false), SETTLE_MS + extra)
    return () => clearTimeout(id)
  }, [step])

  // Interactive steps: the learner's tap on the real button drives the
  // transition. The `settling` guard prevents the effect from firing on the
  // first render of a new step before the screen has caught up.
  useEffect(() => {
    if (!step || settling) return
    if (step.kind === 'collapse' && !expanded) onAdvance()
    if (step.kind === 'expand' && expanded) onAdvance()
  }, [step, expanded, settling, onAdvance])

  if (!step) return null

  const isNarrative = step.kind === 'narrative' || rect === null
  const isInteractive = step.kind === 'collapse' || step.kind === 'expand'
  const isLast = stepIndex === steps.length - 1
  const pos = cardStyle(isNarrative ? null : rect, vpHeight)

  return (
    <div
      // Marks this subtree as the player's own chrome. `TaskOverlay`'s
      // capture-phase blocker swallows every tap that is not on an allowed
      // target while the learner is off a required route — and this tour is
      // rendered as a SIBLING of `[data-task-overlay]`, so without this marker
      // the blocker ate the tour's own Next / skip / scrim taps and the
      // onboarding froze on step 1 of 6 for every lesson that starts off-route.
      data-unguided-tour
      className="fixed inset-0 z-[80] transition-opacity duration-300 pointer-events-none"
      style={{ opacity: settling ? 0 : 1 }}
      aria-live="polite"
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* ── Scrim ─────────────────────────────────────────────────────────── */}
      {isNarrative ? (
        <button
          type="button"
          onClick={onAdvance}
          className={cn('absolute inset-0 h-full w-full cursor-default pointer-events-auto', SCRIM)}
          aria-label={t('tourNext')}
        />
      ) : (
        // rect is non-null here: isNarrative is only false when rect !== null
        rect !== null && <Spotlight rect={rect} passThrough={isInteractive} />
      )}

      {/* ── Card ──────────────────────────────────────────────────────────── */}
      <div
        className="absolute flex justify-center px-4 pointer-events-none"
        style={{ ...pos, left: 0, right: 0 }}
      >
        <div className="w-full max-w-[360px] rounded-2xl bg-card p-5 shadow-xl pointer-events-auto">
          {/* Step counter + dismiss */}
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className={cn('tabular-nums text-muted-foreground', META_SIZES[fontScale])}>
              {stepIndex + 1} / {steps.length}
            </span>
            <button
              type="button"
              onClick={onDismiss}
              aria-label={t('tourSkip')}
              className="flex size-8 items-center justify-center rounded-lg text-muted-foreground"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <p className={cn('mb-1 font-bold text-foreground', TITLE_SIZES[fontScale])}>
            {step.heading}
          </p>
          <p
            className={cn(
              'text-muted-foreground',
              DESC_SIZES[fontScale],
              isInteractive ? 'mb-2' : 'mb-4',
            )}
          >
            {step.body}
          </p>

          {isInteractive ? (
            // No button — the learner must perform the real action to advance
            <p className={cn('italic text-muted-foreground/70', META_SIZES[fontScale])}>
              {t('tourTapToContinue')}
            </p>
          ) : (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onAdvance}
                className="flex min-h-[var(--spacing-touch)] items-center justify-center rounded-xl bg-primary px-5 font-semibold text-primary-foreground text-sm"
              >
                {isLast ? t('tourGotIt') : t('tourNext')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
